import { impersonationInitUserApi, useMe } from "@/api/services/admin/auth";
import { fetchOrganizationDetailApi } from "@/api/services/admin/organization";
import { useEnum } from "@/api/services/global/enum";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { ELoginPurpose } from "@/constants/enum";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { AppRoutes } from "@/routes/config";
import { text } from "@/theme/colors";
import { FCC } from "@/types/common";
import { EProductCode } from "@/types/enum";
import { getApiErrorMessage } from "@/utils/error";
import { findLabelByValue } from "@/utils/object";
import { openNewTab } from "@/utils/url";
import { Alert, AlertTitle, Box, Stack, SxProps, Theme, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

type Props = {
  organizationId?: string;
  productCode?: string;
  userRegistrationToken: string;
  purposes?: string[];
  otherText?: string | null;
  sx?: SxProps<Theme>;
};

export const FirstLoginCard: FC<Props> = ({ organizationId, productCode, userRegistrationToken, purposes, otherText, sx }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: me } = useMe();
  const { data: loginPurposes } = useEnum("ELoginPurpose");
  const { data: orgData, isLoading: isLoadingOrg } = useQuery({
    queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_DETAIL, organizationId],
    queryFn: () => fetchOrganizationDetailApi(organizationId as string),
    enabled: Boolean(organizationId),
  });

  const organization = orgData?.data;
  const productContractName = useMemo(() => {
    if (productCode === EProductCode.CENTER) {
      return "irohana center";
    }
    const contract = organization?.productContracts.find((item) => String(item.product?.code) === String(productCode));
    return contract?.product?.name || EMPTY_STR.TEXT;
  }, [organization?.productContracts]);

  // Resolve purpose enum values to labels; "その他" is replaced by the free-text note.
  const purposesText = useMemo(() => {
    return (purposes ?? [])
      .map((purpose) =>
        purpose === ELoginPurpose.OTHER ? otherText || findLabelByValue(loginPurposes, purpose) : findLabelByValue(loginPurposes, purpose),
      )
      .filter(Boolean)
      .join("、");
  }, [purposes, otherText, loginPurposes]);

  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasAutoRunRef = useRef(false);

  // Provision the product admin account via /impersonation/init-user, then open the product in a new tab.
  // The current tab stays here; the button below re-runs this to re-fetch the redirect (e.g. popup blocked).
  const handleInitUser = async (withDelay = false) => {
    if (!userRegistrationToken) return;
    try {
      setIsProcessing(true);
      setErrorMessage(null);
      const timeDelay = withDelay ? 5000 : 0;
      const [data] = await Promise.all([
        impersonationInitUserApi({ userRegistrationToken }),
        // Keep the loading state for at least 5s before redirecting.
        new Promise((resolve) => setTimeout(resolve, timeDelay)),
      ]);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PRODUCT_CONTRACT.FETCH_LIST_BY_ORGANIZATION] });
      openNewTab(data.redirectUrl, `session:${productCode}`);
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error));
    } finally {
      setIsProcessing(false);
    }
  };

  // Auto-run once on mount; the button acts as a manual retry after success.
  useEffect(() => {
    if (hasAutoRunRef.current || !userRegistrationToken) return;
    hasAutoRunRef.current = true;
    handleInitUser(true);
  }, [userRegistrationToken]);

  // Disabled while creating and while in an error state (creation failed).
  const isMoveDisabled = isProcessing || Boolean(errorMessage);

  return (
    <InnerBox
      padding="32px"
      sx={{ gap: 3, ...sx }}
      title={
        <Typography variant="head20Bold" color={text.primary} width="100%" textAlign="center">
          初回ログインの処理中..
        </Typography>
      }
    >
      {errorMessage && (
        <Alert severity="error" sx={{ whiteSpace: "pre-line" }}>
          <AlertTitle>ユーザー作成に失敗</AlertTitle>
          {errorMessage}
        </Alert>
      )}

      {!me || !organization || isLoadingOrg ? (
        <LoadingArea />
      ) : (
        <Stack pt="8px">
          <Box display="grid" gridTemplateColumns="1fr 1fr" rowGap="16px" columnGap="24px">
            <FieldItem label="組織ID">
              <Input nonControl readonly size="lg" value={organization.externalId ?? ""} />
            </FieldItem>
            <FieldItem label="組織名">
              <Input nonControl readonly size="lg" value={organization.name ?? ""} />
            </FieldItem>
            <FieldItem label="対象プロダクト">
              <Input nonControl readonly size="lg" value={productContractName} />
            </FieldItem>
            <FieldItem label="管理ユーザーのメールアドレス">
              <Input nonControl readonly size="lg" value={me.data.email ?? ""} />
            </FieldItem>
            <Box gridColumn="1 / -1">
              <FieldItem label="ログイン目的">
                <Input nonControl readonly size="lg" value={purposesText} />
              </FieldItem>
            </Box>
          </Box>

          <Typography variant="body14" color={text.secondary} pt="16px" component="div">
            管理ユーザーを作成しています。自動で遷移しない場合は、以下からログインしてください。
          </Typography>

          <Stack gap={2} pt="32px">
            <Button
              variant="secondary"
              fullWidth
              isLoading={isProcessing}
              disabled={isMoveDisabled}
              onClick={() => handleInitUser(false)}
              sx={{ height: "37px !important" }}
            >
              <Typography variant="body14Semi">{productContractName} へ移動</Typography>
            </Button>
            <Button variant="ghost" fullWidth onClick={() => navigate(AppRoutes.organizationSelect)} sx={{ height: "37px !important" }}>
              <Typography variant="body14Semi">組織選択へ戻る</Typography>
            </Button>
          </Stack>
        </Stack>
      )}
    </InnerBox>
  );
};

const FieldItem: FCC<{ label: string }> = ({ label, children }) => (
  <Stack gap="4px">
    <Typography variant="cap12" color={text.primary}>
      {label}
    </Typography>
    {children}
  </Stack>
);
